import React, { useState, useRef, useEffect } from "react";
import { Circle, CircleDashed, InfoIcon } from "lucide-react";
import {
	FormField,
	FieldTypeEnum,
} from "../../pages/form-builder/types/formTypes";
import { ToothData } from "@/dentconnect/src/pages/job-request/types";
import { Tooth } from "./components/Tooth";
import { MiniTooth } from "./components/MiniTooth";
import { ToothDetail } from "./components/ToothDetail";
import { quadrant1, quadrant2, quadrant3, quadrant4 } from "./constants";
import { toothHasInfo as checkToothHasInfo } from "./helpers";

interface OdontogramProps {
	selectedTeeth: ToothData[];
	onTeethChange?: (teeth: ToothData[]) => void;
	disabled?: boolean;
	maxSelections?: number;
	odontogramFields?: FormField[];
}

export const Odontogram: React.FC<OdontogramProps> = ({
	selectedTeeth = [],
	onTeethChange,
	disabled = false,
	maxSelections,
	odontogramFields = [],
}) => {
	// State for the active tooth (for which we're editing dropdown values)
	const [activeTooth, setActiveTooth] = useState<number | null>(null);
	const detailsRef = useRef<HTMLDivElement>(null);

	// Convert teeth data structure based on presence of odontogram fields
	const hasOdontogramFields = odontogramFields && odontogramFields.length > 0;

	// We're now always using ToothData format
	const teethData: ToothData[] = selectedTeeth || [];
	const selectedToothNumbers: number[] = teethData.map((t) => t.toothNumber);

	// Determine if a tooth has additional information
	const toothHasInfo = (toothNumber: number): boolean => {
		return checkToothHasInfo(toothNumber, hasOdontogramFields, teethData);
	};

	// Get values for a specific tooth
	const getToothValues = (toothNumber: number): Record<string, string> => {
		const toothData = teethData.find((t) => t.toothNumber === toothNumber);
		return toothData?.values || {};
	};

	// Handle tooth click
	const handleToothClick = (toothNumber: number) => {
		if (disabled) return;

		// If we have odontogram fields and tooth is already selected, show detail dialog
		if (hasOdontogramFields && selectedToothNumbers.includes(toothNumber)) {
			setActiveTooth(toothNumber);
			return;
		}

		let newTeethData: ToothData[];

		if (selectedToothNumbers.includes(toothNumber)) {
			// Deselect tooth
			newTeethData = teethData.filter(
				(t) => t.toothNumber !== toothNumber
			);
		} else {
			// Check if we've reached the maximum number of selections
			if (maxSelections && selectedToothNumbers.length >= maxSelections) {
				return;
			}

			// Select tooth
			const newTooth: ToothData = {
				toothNumber,
				values: {},
			};

			newTeethData = [...teethData, newTooth];

			// If we have odontogram fields, show the detail dialog for the new tooth
			if (hasOdontogramFields) {
				setActiveTooth(toothNumber);
			}
		}

		// Always return data in ToothData format for consistency
		onTeethChange?.(newTeethData);
	};

	// Handle dropdown values change
	const handleToothValuesChange = (
		toothNumber: number,
		values: Record<string, string>
	) => {
		const newTeethData = teethData.map((t) =>
			t.toothNumber === toothNumber ? { ...t, values } : t
		);

		// Always return data in ToothData format for consistency
		onTeethChange?.(newTeethData);
	};

	// Close detail when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				detailsRef.current &&
				!detailsRef.current.contains(event.target as Node)
			) {
				setActiveTooth(null);
			}
		};

		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Helper function to render a tooth quadrant
	const renderTeethQuadrant = (toothNumbers: number[]) => {
		return toothNumbers.map((toothNumber) => (
			<Tooth
				key={toothNumber}
				number={toothNumber}
				selected={selectedToothNumbers.includes(toothNumber)}
				hasInfo={toothHasInfo(toothNumber)}
				onClick={handleToothClick}
				disabled={disabled}
			/>
		));
	};

	return (
		<div className="bg-white p-4 rounded-lg border">
			<div className="flex flex-col gap-6">
				{/* Legend */}
				<div className="flex items-center gap-3 justify-center text-sm text-gray-500">
					<div className="flex items-center gap-1">
						<CircleDashed className="h-4 w-4" />
						<span>Nicht ausgewählt</span>
					</div>
					<div className="flex items-center gap-1">
						<Circle className="h-4 w-4 stroke-primary fill-primary/20" />
						<span>Ausgewählt</span>
					</div>
					{hasOdontogramFields && (
						<div className="flex items-center gap-1">
							<div className="relative">
								<Circle className="h-4 w-4 stroke-primary fill-primary/20" />
								<InfoIcon className="absolute -top-1 -right-1 h-2 w-2 text-blue-500 fill-blue-500" />
							</div>
							<span>Information hinterlegt</span>
						</div>
					)}
				</div>

				{/* Top row: Q1 (18-11) and Q2 (21-28) */}
				<div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
					<div className="grid grid-cols-8 gap-1 pr-3">
						{/* Q1: Top Left (18-11) */}
						{renderTeethQuadrant(quadrant1)}
					</div>
					<div className="grid grid-cols-8 gap-1 pl-3">
						{/* Q2: Top Right (21-28) */}
						{renderTeethQuadrant(quadrant2)}
					</div>
				</div>

				{/* Divider for upper and lower teeth */}
				<div className="border-t border-gray-200" />

				{/* Bottom row: Q4 (48-41) and Q3 (31-38) */}
				<div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
					<div className="grid grid-cols-8 gap-1 pr-3">
						{/* Q4: Bottom Left (48-41) */}
						{renderTeethQuadrant(quadrant4)}
					</div>
					<div className="grid grid-cols-8 gap-1 pl-3">
						{/* Q3: Bottom Right (31-38) */}
						{renderTeethQuadrant(quadrant3)}
					</div>
				</div>

				{/* Tooth detail popup */}
				{activeTooth !== null && hasOdontogramFields && (
					<div className="mt-4" ref={detailsRef}>
						<ToothDetail
							toothNumber={activeTooth}
							odontogramFields={odontogramFields}
							values={getToothValues(activeTooth)}
							onValuesChange={(values) =>
								handleToothValuesChange(activeTooth, values)
							}
							onRemoveTooth={() =>
								onTeethChange?.(
									teethData.filter(
										(t) => t.toothNumber !== activeTooth
									)
								)
							}
							onClose={() => setActiveTooth(null)}
						/>
					</div>
				)}

				{/* Show selected teeth with info */}
				{hasOdontogramFields && teethData.length > 0 && !disabled && (
					<div className="mt-4">
						<h3 className="text-sm font-medium mb-2">
							Ausgewählte Zähne
						</h3>
						<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
							{teethData.map((tooth) => {
								const hasValues =
									Object.keys(tooth.values).length > 0 &&
									Object.values(tooth.values).some(
										(v) => v !== ""
									);
								return (
									<div
										key={tooth.toothNumber}
										className="border rounded-md p-2 text-sm cursor-pointer hover:bg-gray-50"
										onClick={() =>
											setActiveTooth(tooth.toothNumber)
										}
									>
										<div className="font-medium">
											Zahn {tooth.toothNumber}
										</div>
										{hasValues ? (
											<div className="text-xs mt-1">
												{Object.entries(tooth.values)
													.filter(
														([_, value]) =>
															value !== ""
													)
													.map(([fieldId, value]) => {
														const field =
															odontogramFields.find(
																(f) =>
																	f.id ===
																	fieldId
															);
														let displayValue =
															value;

														if (
															field?.type ===
															FieldTypeEnum.SELECT
														) {
															// Get option text by index from the field's options array
															const optionIndex =
																parseInt(
																	value,
																	10
																);
															displayValue =
																field.options?.[
																	optionIndex
																] || value;
														}

														return field ? (
															<div
																key={fieldId}
																className="flex justify-between"
															>
																<span className="text-gray-500">
																	{
																		field.label
																	}
																	:
																</span>
																<span>
																	{
																		displayValue
																	}
																</span>
															</div>
														) : null;
													})}
											</div>
										) : (
											<div className="text-xs text-gray-400 mt-1">
												Keine Informationen
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

// Mini version of the Odontogram specifically for message display

export const MiniOdontogram: React.FC<{
	selectedTeeth: ToothData[];
	size?: "small" | "medium";
	odontogramFields?: FormField[];
}> = ({ selectedTeeth, size = "medium", odontogramFields = [] }) => {
	// Size configuration
	const padding = size === "small" ? "p-2" : "p-3";
	const gap = size === "small" ? "gap-3" : "gap-4";

	// We're now always using ToothData format
	const hasOdontogramFields = odontogramFields && odontogramFields.length > 0;
	const teethData: ToothData[] = selectedTeeth || [];
	const selectedToothNumbers: number[] = teethData.map((t) => t.toothNumber);

	// Helper function to determine if a tooth has information
	const toothHasInfo = (toothNumber: number): boolean => {
		return checkToothHasInfo(toothNumber, hasOdontogramFields, teethData);
	};

	// Helper function to render a quadrant of mini teeth
	const renderMiniTeethQuadrant = (
		toothNumbers: number[],
		showInfo = false
	) => {
		return toothNumbers.map((toothNumber) => (
			<MiniTooth
				key={toothNumber}
				number={toothNumber}
				selected={selectedToothNumbers.includes(toothNumber)}
				hasInfo={showInfo ? toothHasInfo(toothNumber) : false}
				size={size}
			/>
		));
	};

	// Render the odontogram layout
	const renderOdontogramLayout = (showInfo = false) => (
		<>
			{/* Top row: Q1 (18-11) and Q2 (21-28) */}
			<div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
				{/* Q1: Top Left (18-11) */}
				<div className="grid grid-cols-8 gap-0 pr-2">
					{renderMiniTeethQuadrant(quadrant1, showInfo)}
				</div>

				{/* Q2: Top Right (21-28) */}
				<div className="grid grid-cols-8 gap-0 pl-2">
					{renderMiniTeethQuadrant(quadrant2, showInfo)}
				</div>
			</div>

			{/* Divider for upper and lower teeth */}
			<div className="border-t border-gray-200" />

			{/* Bottom row: Q4 (48-41) and Q3 (31-38) */}
			<div className="grid grid-cols-2 gap-0 divide-x divide-gray-200">
				{/* Q4: Bottom Left (48-41) */}
				<div className="grid grid-cols-8 gap-0 pr-2">
					{renderMiniTeethQuadrant(quadrant4, showInfo)}
				</div>

				{/* Q3: Bottom Right (31-38) */}
				<div className="grid grid-cols-8 gap-0 pl-2">
					{renderMiniTeethQuadrant(quadrant3, showInfo)}
				</div>
			</div>
		</>
	);

	// If we have teeth with information, render an extended view with tooth details
	if (
		hasOdontogramFields &&
		teethData.some((t) => toothHasInfo(t.toothNumber))
	) {
		return (
			<div className="w-full">
				<div
					className={`bg-white ${padding} rounded-lg border border-gray-200 w-full mb-2`}
				>
					<div className={`flex flex-col ${gap}`}>
						{renderOdontogramLayout(true)}
					</div>
				</div>

				{/* Table of teeth with information */}
				<div className="border rounded-md overflow-hidden">
					<table className="min-w-full divide-y divide-gray-200">
						<thead className="bg-gray-50">
							<tr>
								<th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
									Zahn
								</th>
								{odontogramFields.map((field) => (
									<th
										key={field.id}
										className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
									>
										{field.label}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="bg-white divide-y divide-gray-200">
							{teethData
								.filter((tooth) =>
									selectedToothNumbers.includes(
										tooth.toothNumber
									)
								)
								.sort((a, b) => a.toothNumber - b.toothNumber)
								.map((tooth) => (
									<tr key={tooth.toothNumber}>
										<td className="px-2 py-1 whitespace-nowrap text-xs font-medium text-gray-900">
											{tooth.toothNumber}
										</td>
										{odontogramFields.map((field) => {
											const value =
												tooth.values[field.id] || "";
											let displayValue = value;

											// Handle different field types
											if (
												field.type ===
													FieldTypeEnum.SELECT &&
												value !== ""
											) {
												const optionIndex = parseInt(
													value,
													10
												);
												displayValue =
													field.options?.[
														optionIndex
													] || value;
											}

											return (
												<td
													key={field.id}
													className="px-2 py-1 whitespace-nowrap text-xs text-gray-500"
												>
													{displayValue || "—"}
												</td>
											);
										})}
									</tr>
								))}
						</tbody>
					</table>
				</div>
			</div>
		);
	}

	// Standard view for simple teeth selection without additional info
	return (
		<div
			className={`bg-white ${padding} rounded-lg border border-gray-200 w-full`}
		>
			<div className={`flex flex-col ${gap}`}>
				{renderOdontogramLayout(false)}
			</div>
		</div>
	);
};

export default Odontogram;
